// Renders the accounts side drawer with create, edit, and delete entry points.
import React from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { appScrollbarSx } from "../../../lib/scrollbarSx";
import type { TAccountCatalogItem } from "../types";

interface SheetAccountsDrawerProps {
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
  taccountCatalog: TAccountCatalogItem[];
  hoveredCatalogTAccountId: string | null;
  onHoveredCatalogTAccountIdChange: (value: string | null) => void;
  onOpenCreateTAccountDialog: () => void;
  onOpenEditTAccountDialog: (item: TAccountCatalogItem) => void;
  onDeleteTAccountRequest: (item: TAccountCatalogItem) => void;
}

export default function SheetAccountsDrawer({
  open,
  onClose,
  readOnly = false,
  taccountCatalog,
  hoveredCatalogTAccountId,
  onHoveredCatalogTAccountIdChange,
  onOpenCreateTAccountDialog,
  onOpenEditTAccountDialog,
  onDeleteTAccountRequest,
}: SheetAccountsDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: 360, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="h6">Accounts</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close accounts panel">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={onOpenCreateTAccountDialog}
            disabled={readOnly}
          >
            Add Account
          </Button>
        </Box>

        <List sx={{ flex: 1, overflow: "auto", pt: 0, ...appScrollbarSx }}>
          {taccountCatalog.map((item) => {
            const showActions = hoveredCatalogTAccountId === item.taccountId;
            return (
              <ListItem
                key={item.taccountId}
                divider
                onMouseEnter={() => onHoveredCatalogTAccountIdChange(item.taccountId)}
                onMouseLeave={() => onHoveredCatalogTAccountIdChange(
                  hoveredCatalogTAccountId === item.taccountId ? null : hoveredCatalogTAccountId
                )}
                secondaryAction={showActions && !readOnly ? (
                  <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${item.name}`}
                        onClick={() => onOpenEditTAccountDialog(item)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        aria-label={`Delete ${item.name}`}
                        onClick={() => onDeleteTAccountRequest(item)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : undefined}
              >
                <ListItemText
                  primary={item.name}
                  secondary={item.alias ? `Account #: ${item.alias}` : ""}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
}
